// The product-logo frame: every logo upload is composited into a transparent
// PNG of this fixed height, with the logo's main part (the wordmark or mark)
// fitted into the core band and only faint stuff (swooshes, shadows,
// taglines) in the margins above and below. Consumers size logos by CSS
// height with width auto (.product-logo-title / .product-logo-section), so
// a shared canvas height + band means every logo renders with the same
// visual weight next to its neighbors. The numbers below are the single
// source of truth — change them and the CSS heights together, then
// re-upload the logos.

export const LOGO_CANVAS_HEIGHT = 256
// the core band as fractions of the canvas height (60% band, 20% margins)
export const LOGO_BAND = { top: 0.2, bottom: 0.8 }
// transparent padding added on both sides after the horizontal trim, as a
// fraction of the canvas height
export const LOGO_SIDE_PADDING = 0.04
// mirrors MIN_LOGO_IMAGE_WIDTH in lib/api/products.ts — the presign rejects
// narrower results (a square mark in the band comes out ~175px wide)
export const MIN_LOGO_WIDTH = 128
// pixels with alpha at or below this count as empty when trimming
const ALPHA_THRESHOLD = 8

// Where the source sits on the canvas: scale = source px → canvas px, x/y =
// the source's top-left in canvas px. x only matters on the editor's
// stage — the export trims left/right to the artwork.
export interface LogoPlacement {
  scale: number
  x: number
  y: number
}

// Pixel bounds of the source's visible (non-transparent) area, in source
// px; right/bottom are exclusive.
export interface InkBounds {
  left: number
  top: number
  right: number
  bottom: number
}

function makeCanvas(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas not supported")
  return [canvas, ctx]
}

// Column/row scan of an RGBA buffer for any pixel above the alpha
// threshold. null = fully transparent.
function inkBoundsOf(data: Uint8ClampedArray, width: number, height: number): InkBounds | null {
  let left = width, top = height, right = -1, bottom = -1
  for (let y = 0; y < height; y++) {
    const rowStart = y * width * 4
    for (let x = 0; x < width; x++) {
      if (data[rowStart + x * 4 + 3] > ALPHA_THRESHOLD) {
        if (x < left) left = x
        if (x > right) right = x
        if (y < top) top = y
        if (y > bottom) bottom = y
      }
    }
  }
  if (right < 0) return null
  return { left, top, right: right + 1, bottom: bottom + 1 }
}

// Decodes the picked file (EXIF-upright, like the avatar path) and measures
// its ink. Throws on files the browser can't decode as an image.
export async function loadLogoSource(file: File): Promise<{ bitmap: ImageBitmap, ink: InkBounds | null }> {
  // "from-image" postdates this project's TS DOM lib, hence the cast
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" } as unknown as ImageBitmapOptions)
  const [, ctx] = makeCanvas(bitmap.width, bitmap.height)
  ctx.drawImage(bitmap, 0, 0)
  const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height)
  return { bitmap, ink: inkBoundsOf(data, bitmap.width, bitmap.height) }
}

// The starting placement: the source's whole ink height fitted into the
// band and centered in a frame frameWidth canvas px wide — the admin then
// enlarges it so only the faint parts spill into the margins. A fully
// transparent source is fitted by its full extent instead.
export function initialPlacement(bitmap: ImageBitmap, ink: InkBounds | null, frameWidth: number): LogoPlacement {
  const bounds = ink ?? { left: 0, top: 0, right: bitmap.width, bottom: bitmap.height }
  const bandTop = LOGO_BAND.top * LOGO_CANVAS_HEIGHT
  const bandHeight = (LOGO_BAND.bottom - LOGO_BAND.top) * LOGO_CANVAS_HEIGHT
  const scale = bandHeight / Math.max(1, bounds.bottom - bounds.top)
  return {
    scale,
    x: (frameWidth - (bounds.right - bounds.left) * scale) / 2 - bounds.left * scale,
    y: bandTop - bounds.top * scale
  }
}

// Composites the source at its placement onto the fixed-height canvas,
// trims the columns outside the artwork (rows are never trimmed — the
// canvas height is the contract), pads the sides and encodes a PNG. Throws
// when nothing visible is left inside the frame.
export async function renderLogoCanvas(bitmap: ImageBitmap, placement: LogoPlacement): Promise<{ blob: Blob, width: number }> {
  const drawnWidth = bitmap.width * placement.scale
  const [full, fullCtx] = makeCanvas(drawnWidth, LOGO_CANVAS_HEIGHT)
  fullCtx.imageSmoothingQuality = "high"
  fullCtx.drawImage(bitmap, 0, placement.y, drawnWidth, bitmap.height * placement.scale)
  const { data } = fullCtx.getImageData(0, 0, full.width, full.height)
  const ink = inkBoundsOf(data, full.width, full.height)
  if (ink === null) throw new Error("Nothing of the logo is inside the frame")
  const pad = Math.round(LOGO_SIDE_PADDING * LOGO_CANVAS_HEIGHT)
  const inkWidth = ink.right - ink.left
  const [out, outCtx] = makeCanvas(inkWidth + 2 * pad, LOGO_CANVAS_HEIGHT)
  outCtx.drawImage(full, ink.left, 0, inkWidth, LOGO_CANVAS_HEIGHT, pad, 0, inkWidth, LOGO_CANVAS_HEIGHT)
  const blob = await new Promise<Blob>((resolve, reject) => {
    out.toBlob((b) => (b ? resolve(b) : reject(new Error("Image encoding failed"))), "image/png")
  })
  return { blob, width: out.width }
}
